import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Pencil,
  RefreshCw,
  Sparkles,
  Trash2,
  Download,
  Upload,
  CheckCircle2,
  FileText,
  Camera as CameraIcon,
  StickyNote,
  Clapperboard,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { storeMediaBlob, getMediaBlob, deleteMediaBlob } from "@/lib/mediaVault";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader, ErrorState } from "@/components/states";
import { RecordingStudio } from "@/components/production/RecordingStudio";
import { PhotoCapture } from "@/components/production/PhotoCapture";


const STATUS_ORDER = [
  "script_ready",
  "pre_production",
  "recording",
  "editing",
  "review",
  "ready",
];
const STATUS_PIPELINE = STATUS_ORDER;
const STATUS_LABEL = {
  script_ready: "Script Ready",
  pre_production: "Pre-Production",
  recording: "Recording",
  editing: "Editing",
  review: "Review",
  ready: "Ready",
};

const STATUS_PROGRESS = {
  script_ready: 20,
  pre_production: 40,
  recording: 60,
  editing: 80,
  review: 90,
  ready: 100,
};

const wordsOf = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;
const estimateMinutes = (words) => (words / 140).toFixed(1); // ~140 wpm speaking pace
const formatDate = (v) => (v ? new Date(v).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

function SectionCard({ title, icon: Icon, right, children, testid }) {
  return (
    <section className="rounded-xl border border-border bg-surface p-4 sm:p-6 shadow-sm min-w-0" data-testid={testid}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5 sm:gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon className="h-4 w-4 shrink-0 text-accent" />}
          <h2 className="truncate font-display text-base font-semibold text-foreground">{title}</h2>
        </div>
        {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
      </div>
      {children}
    </section>
  );
}

export default function ProductionWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null });
  const [project, setProject] = useState(null);
  const [content, setContent] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [replacingIdx, setReplacingIdx] = useState(null);
  const assetBlobsRef = useRef({});

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const [projectRes, downloadsRes] = await Promise.all([
        api.get(`/production/${id}`),
        api.get(`/production/${id}/downloads`).catch(() => ({ data: { downloads: [] } })),
      ]);
      setProject(projectRes.data.project);
      setContent(projectRes.data.content);
      setNotes(projectRes.data.project?.notes || "");
      setDownloads(downloadsRes.data.downloads || []);
      setState({ loading: false, error: null });
    } catch (error) {
      setState({ loading: false, error: apiErrorMessage(error) });
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshContent = async () => {
    try {
      const { data } = await api.get(`/content/${project.content_id}`);
      setContent(data);
      toast.success("Content refreshed");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const updateProject = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/production/${id}`, patch);
      setProject(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const { data } = await api.put(`/production/${id}`, { notes });
      setProject(data);
      toast.success("Notes saved");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingNotes(false);
    }
  };

  const runReview = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/production/${id}/content-review`);
      setProject((p) => ({ ...p, content_review: data.review }));
      toast.success("AI review updated");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const applyReplacement = async (index, imp) => {
    if (!content) return;
    const excerpt = (imp.excerpt || "").trim();
    const replacement = (imp.replacement || imp.suggestion || "").trim();

    if (!replacement) {
      toast.error("No replacement line available.");
      return;
    }

    setReplacingIdx(index);
    try {
      const currentBody = content.body || "";
      let newBody = currentBody;

      if (excerpt && currentBody.includes(excerpt)) {
        newBody = currentBody.replace(excerpt, replacement);
      } else if (excerpt) {
        const lowerBody = currentBody.toLowerCase();
        const lowerExcerpt = excerpt.toLowerCase();
        const matchIdx = lowerBody.indexOf(lowerExcerpt);
        if (matchIdx !== -1) {
          newBody =
            currentBody.substring(0, matchIdx) +
            replacement +
            currentBody.substring(matchIdx + excerpt.length);
        } else if (imp.area === "hook" || imp.problem?.toLowerCase().includes("hook")) {
          const lines = currentBody.split("\n");
          lines[0] = replacement;
          newBody = lines.join("\n");
        } else {
          newBody = currentBody + "\n\n" + replacement;
        }
      } else {
        if (imp.area === "hook" || imp.problem?.toLowerCase().includes("hook")) {
          const lines = currentBody.split("\n");
          lines[0] = replacement;
          newBody = lines.join("\n");
        } else {
          newBody = currentBody + "\n\n" + replacement;
        }
      }

      // 1. Persist updated content script
      const { data: updatedContent } = await api.put(`/content/${project.content_id}`, {
        ...content,
        body: newBody,
      });
      setContent(updatedContent);

      // 2. Mark this improvement as done
      const currentImprovements = [...(project.content_review?.improvements || [])];
      currentImprovements[index] = {
        ...currentImprovements[index],
        done: true,
        applied: true,
      };

      const updatedReview = {
        ...project.content_review,
        improvements: currentImprovements,
      };

      setProject((p) => ({
        ...p,
        content_review: updatedReview,
      }));

      // 3. Save marked done state in backend review
      await api.put(`/production/${id}/content-review`, {
        improvements: currentImprovements,
      });

      toast.success("Applied replacement line to script & marked as done!");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setReplacingIdx(null);
    }
  };


  const saveVideoAsset = async ({ filename, mimeType, sizeBytes, durationMs, blobUrl, blob }) => {
    try {
      const cleanFilename = filename && filename.endsWith(".mp4") ? filename : `${(filename || "take").replace(/\.[^/.]+$/, "")}.mp4`;
      const { data } = await api.post(`/production/${id}/assets`, {
        type: "video",
        filename: cleanFilename,
        mime_type: "video/mp4",
        size_bytes: sizeBytes,
        duration_ms: durationMs,
        source: "browser_recording",
      });
      if (blob) {
        await storeMediaBlob(data.id, blob, { filename: cleanFilename, mimeType: "video/mp4" });
        await storeMediaBlob(cleanFilename, blob, { filename: cleanFilename, mimeType: "video/mp4" });
      }
      assetBlobsRef.current[data.id] = { blob, blobUrl, filename: cleanFilename, mimeType: "video/mp4" };
      assetBlobsRef.current[cleanFilename] = { blob, blobUrl, filename: cleanFilename, mimeType: "video/mp4" };
      setProject((p) => ({ ...p, assets: [...(p.assets || []), data] }));
      toast.success("MP4 Video saved to assets! You can download the .mp4 file anytime below.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const savePhotoAsset = async ({ filename, mimeType, sizeBytes, width, height, dataUrl, blob }) => {
    try {
      const cleanFilename = filename && filename.endsWith(".png") ? filename : `${(filename || "photo").replace(/\.[^/.]+$/, "")}.png`;
      const { data } = await api.post(`/production/${id}/assets`, {
        type: "photo",
        filename: cleanFilename,
        mime_type: "image/png",
        size_bytes: sizeBytes,
        width,
        height,
        source: "browser_capture",
      });
      if (blob) {
        await storeMediaBlob(data.id, blob, { filename: cleanFilename, mimeType: "image/png" });
        await storeMediaBlob(cleanFilename, blob, { filename: cleanFilename, mimeType: "image/png" });
      }
      assetBlobsRef.current[data.id] = { blob, blobUrl: dataUrl, filename: cleanFilename, mimeType: "image/png" };
      assetBlobsRef.current[cleanFilename] = { blob, blobUrl: dataUrl, filename: cleanFilename, mimeType: "image/png" };
      setProject((p) => ({ ...p, assets: [...(p.assets || []), data] }));
      toast.success("Photo saved to assets! You can download it anytime below.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const handleUploadAsset = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const isVid = file.type.startsWith("video") || file.name.endsWith(".mp4");
      let cleanName = file.name;
      if (isVid && !cleanName.endsWith(".mp4")) {
        cleanName = `${cleanName.replace(/\.[^/.]+$/, "")}.mp4`;
      }
      const mime = isVid ? "video/mp4" : file.type || "image/png";
      const { data } = await api.post(`/production/${id}/assets`, {
        type: isVid ? "video" : "photo",
        filename: cleanName,
        mime_type: mime,
        size_bytes: file.size,
        source: "manual",
      });
      await storeMediaBlob(data.id, file, { filename: cleanName, mimeType: mime });
      await storeMediaBlob(cleanName, file, { filename: cleanName, mimeType: mime });
      assetBlobsRef.current[data.id] = { blob: file, blobUrl: URL.createObjectURL(file), filename: cleanName, mimeType: mime };
      assetBlobsRef.current[cleanName] = { blob: file, blobUrl: URL.createObjectURL(file), filename: cleanName, mimeType: mime };
      setProject((p) => ({ ...p, assets: [...(p.assets || []), data] }));
      toast.success(`Uploaded ${cleanName} to saved assets!`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      e.target.value = "";
    }
  };

  const deleteAsset = async (assetId) => {
    try {
      await api.delete(`/production/${id}/assets/${assetId}`);
      await deleteMediaBlob(assetId);
      delete assetBlobsRef.current[assetId];
      setProject((p) => ({ ...p, assets: (p.assets || []).filter((a) => a.id !== assetId) }));
      toast.success("Asset removed.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const logDownload = async (info) => {
    try {
      const { data } = await api.post(`/production/${id}/downloads`, info);
      setDownloads((d) => [data, ...d]);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const downloadAsset = async (asset) => {
    try {
      // 1. Check in-memory ref
      let blob = assetBlobsRef.current[asset.id]?.blob || assetBlobsRef.current[asset.filename]?.blob;
      let url = assetBlobsRef.current[asset.id]?.blobUrl || assetBlobsRef.current[asset.filename]?.blobUrl;

      // 2. Check IndexedDB persistent mediaVault
      if (!blob) {
        blob = (await getMediaBlob(asset.id)) || (await getMediaBlob(asset.filename));
      }

      const isVideo = asset.type === "video" || (asset.filename || "").endsWith(".mp4");
      let filename = asset.filename || (isVideo ? "take.mp4" : "photo.png");
      if (isVideo && !filename.endsWith(".mp4")) {
        filename = `${filename.replace(/\.[^/.]+$/, "")}.mp4`;
      }

      if (blob) {
        const mimeType = isVideo ? "video/mp4" : (blob.type || "image/png");
        const typedBlob = new Blob([blob], { type: mimeType });
        const downloadUrl = URL.createObjectURL(typedBlob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
        toast.success(`Downloaded ${filename} (${isVideo ? "MP4 Video" : "Image"})`);
      } else if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success(`Downloaded ${filename}`);
      } else {
        toast.error(`No recorded video file found on this browser for "${filename}". Please record a take in the studio above or upload an MP4 file.`);
        return;
      }

      await logDownload({
        asset_id: asset.id,
        filename,
        file_type: isVideo ? "video/mp4" : "image/png",
      });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const downloadAllAssets = async () => {
    const assets = project?.assets || [];
    if (assets.length === 0) {
      toast.error("No assets to download.");
      return;
    }
    for (const a of assets) {
      await downloadAsset(a);
    }
  };

  const words = useMemo(() => wordsOf(content?.body), [content]);
  const minutes = useMemo(() => estimateMinutes(words), [words]);

  if (state.loading)
    return (
      <AppShell title="Production Workspace">
        <Loader label="Loading workspace" />
      </AppShell>
    );
  if (state.error || !project)
    return (
      <AppShell title="Production Workspace">
        <div className="space-y-4">
          <ErrorState message={state.error || "Workspace not found"} onRetry={load} />
          <div className="flex justify-center">
            <Button variant="outline" onClick={() => navigate("/production")} data-testid="workspace-error-back">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Production
            </Button>
          </div>
        </div>
      </AppShell>
    );

  const reviewOutdated = project.content_review?.outdated;

  return (
    <AppShell title="Production Workspace">
      {/* Header */}
      <div className="mb-6 border-b border-border pb-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate("/production")} data-testid="workspace-back" className="shrink-0 h-8 px-2 sm:px-3">
              <ArrowLeft className="mr-1 sm:mr-2 h-4 w-4" /> <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-widest text-accent">Production</p>
                <span className="inline-flex md:hidden rounded-full bg-accent/15 px-2 py-0.2 text-[10px] font-bold text-accent">
                  {STATUS_PROGRESS[project.status] || 0}%
                </span>
              </div>
              <h1 className="mt-0.5 truncate font-display text-lg sm:text-2xl font-bold text-foreground" data-testid="workspace-title">
                {project.title || "Untitled"}
              </h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full xs:w-auto justify-end">
            <Select value={project.status} onValueChange={(v) => updateProject({ status: v })}>
              <SelectTrigger className="w-[130px] sm:w-[160px] h-8 sm:h-9 text-xs" data-testid="workspace-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`workspace-status-${s}`} className="text-xs">
                    {STATUS_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => navigate(`/content/${project.content_id}`)} data-testid="workspace-edit-content" className="h-8 sm:h-9 text-xs">
              <Pencil className="mr-1.5 h-3.5 w-3.5" /> <span className="hidden sm:inline">Edit Content</span><span className="sm:hidden">Edit</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="grid gap-6 md:grid-cols-[2fr_1fr] lg:grid-cols-3">
        {/* MAIN column */}
        <div className="space-y-6 md:col-span-1 lg:col-span-2 min-w-0">
          {/* 1. PRIMARY RECORDING STUDIO (Camera + Overlay Teleprompter + Realtime Recording Deck + Recorded Take) */}
          <RecordingStudio
            script={content?.body || ""}
            title={content?.title || ""}
            contentId={project.content_id}
            onNavigateToEditor={() => navigate(`/content/${project.content_id}`)}
            onDownloadInitiated={logDownload}
            onSaveAsset={saveVideoAsset}
            saving={busy}
          />

          {/* 2. Script Overview & Word Count */}
          <SectionCard title="Script" icon={FileText} testid="section-script"
            right={
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground" data-testid="script-wordcount">
                  {words} words · ~{minutes} min
                </span>
                <Button size="sm" variant="ghost" onClick={refreshContent} data-testid="script-refresh" className="h-7 text-xs">
                  <RefreshCw className="mr-1.5 h-3 w-3" /> <span className="hidden sm:inline">Refresh Content</span><span className="sm:hidden">Refresh</span>
                </Button>
              </div>
            }
          >
            <p className="text-xs text-muted-foreground">
              Title: <span className="text-foreground">{content?.title || "Untitled"}</span> · Platform:{" "}
              <span className="text-foreground">{project.platform || (content?.platforms || []).join(", ") || "—"}</span>
            </p>
            <div className="mt-3 max-h-56 overflow-y-auto overflow-x-hidden break-words rounded-md border border-border bg-background p-3.5 sm:p-4 text-xs sm:text-sm leading-6 sm:leading-7 whitespace-pre-wrap font-mono" data-testid="script-body">
              {(content?.body || "").trim() || (
                <span className="text-muted-foreground">
                  No script yet. Open the Content editor to write your draft.
                </span>
              )}
            </div>
          </SectionCard>

          {/* 3. AI Content Review & One-Click Line Replacement */}
          <SectionCard
            title="AI Content Review"
            icon={Sparkles}
            testid="section-review"
            right={
              <div className="flex flex-wrap items-center gap-2">
                {reviewOutdated && (
                  <span className="rounded-full border border-destructive/40 bg-destructive/5 px-2 py-0.5 text-[10px] uppercase tracking-widest text-destructive" data-testid="review-outdated">
                    Review outdated
                  </span>
                )}
                <Button
                  size="sm"
                  onClick={runReview}
                  disabled={analyzing || !(content?.body || "").trim()}
                  data-testid="review-analyze"
                  className="h-7 sm:h-8 text-xs"
                >
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                  {analyzing ? "Analyzing…" : project.content_review ? "Re-analyze" : "Analyze Content"}
                </Button>
              </div>
            }
          >
            {!project.content_review ? (
              <p className="text-sm text-muted-foreground">
                Run the AI review to get platform-aware recommendations. Recommendations are advice — the app never rewrites your content automatically.
              </p>
            ) : (
              <div className="space-y-4" data-testid="review-content">
                <p className="text-sm text-foreground" data-testid="review-overall">
                  <span className="font-semibold">Overall: </span>
                  {project.content_review.overall || "—"}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Analyzed {formatDate(project.content_review.generated_at)} · Platform focus:{" "}
                  {project.content_review.platform || "general"}
                </p>
                {project.content_review.working?.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      What's working
                    </p>
                    <ul className="list-inside list-disc space-y-1 text-sm" data-testid="review-working">
                      {project.content_review.working.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {project.content_review.improvements?.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Recommendations
                    </p>
                    <div className="space-y-3" data-testid="review-recommendations">
                      {project.content_review.improvements.map((imp, i) => {
                        const isDone = Boolean(imp.done || imp.applied);
                        const replacementLine = (imp.replacement || imp.suggestion || "").trim();
                        return (
                          <div
                            key={i}
                            className={`rounded-md border p-4 transition-all ${
                              isDone
                                ? "border-accent/40 bg-accent/5"
                                : "border-border bg-background"
                            }`}
                            data-testid={`review-recommendation-${i}`}
                          >
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
                                <span className="rounded-full border border-border px-2 py-0.5 text-muted-foreground">
                                  {imp.area}
                                </span>
                                <span
                                  className={`rounded-full px-2 py-0.5 ${
                                    imp.priority === "high"
                                      ? "border border-destructive/40 bg-destructive/5 text-destructive"
                                      : imp.priority === "medium"
                                      ? "border border-accent/40 bg-accent/5 text-accent"
                                      : "border border-border text-muted-foreground"
                                  }`}
                                >
                                  {imp.priority}
                                </span>
                              </div>
                              {isDone && (
                                <span className="flex items-center gap-1 text-[11px] font-semibold text-accent" data-testid={`review-applied-${i}`}>
                                <CheckCircle2 className="h-3.5 w-3.5" /> Done / Applied
                              </span>
                            )}
                          </div>

                          <p className={`text-sm font-semibold text-foreground ${isDone ? "line-through text-muted-foreground" : ""}`}>
                            {imp.problem}
                          </p>
                          {imp.why && <p className="mt-1 text-xs text-muted-foreground">{imp.why}</p>}
                          {imp.suggestion && (
                            <p className="mt-2 text-sm text-foreground">
                              <span className="font-semibold text-accent">Advice: </span>
                              {imp.suggestion}
                            </p>
                          )}

                          {/* Original excerpt in content */}
                          {imp.excerpt && (
                            <div className="mt-2.5 space-y-1">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                Current script line:
                              </p>
                              <blockquote className={`border-l-2 border-border bg-elevated/70 p-2 text-xs italic text-muted-foreground ${isDone ? "line-through opacity-70" : ""}`}>
                                &ldquo;{imp.excerpt}&rdquo;
                              </blockquote>
                            </div>
                          )}

                          {/* Suggested line replacement box with Replace button */}
                          {replacementLine && (
                            <div className="mt-3 space-y-2 rounded-md border border-accent/25 bg-accent/5 p-3">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-accent">
                                  Suggested Line:
                                </span>
                                <Button
                                  size="sm"
                                  onClick={() => applyReplacement(i, imp)}
                                  disabled={replacingIdx === i || isDone}
                                  data-testid={`review-replace-btn-${i}`}
                                  className={isDone
                                    ? "h-7 border border-border bg-muted px-2.5 text-xs text-muted-foreground cursor-default"
                                    : "h-7 bg-accent px-3 text-xs font-semibold text-accent-foreground hover:bg-accent-hover shadow-sm"
                                  }
                                >
                                  {isDone ? (
                                    <>
                                      <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Replaced
                                    </>
                                  ) : replacingIdx === i ? (
                                    "Replacing…"
                                  ) : (
                                    <>
                                      <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Replace in script
                                    </>
                                  )}
                                </Button>
                              </div>
                              <p className="text-xs font-medium text-foreground italic">
                                &ldquo;{replacementLine}&rdquo;
                              </p>
                            </div>
                          )}

                          <div className="mt-3 flex items-center justify-between border-t border-border/50 pt-2.5">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/content/${project.content_id}`)}
                              data-testid={`review-open-editor-${i}`}
                              className="h-7 text-xs text-muted-foreground hover:text-foreground"
                            >
                              <Pencil className="mr-1.5 h-3 w-3" /> Open Content Editor
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </SectionCard>

        {/* 4. Photo Capture (Secondary) */}
        <PhotoCapture
          onDownloadInitiated={logDownload}
          onSaveAsset={savePhotoAsset}
          saving={busy}
        />

        {/* 5. Media Assets */}
        <SectionCard
          title="Media Assets"
          icon={CameraIcon}
          testid="section-assets"
          right={
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="file"
                id="media-asset-file-input"
                accept="video/mp4,video/*,image/*"
                onChange={handleUploadAsset}
                className="hidden"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => document.getElementById("media-asset-file-input")?.click()}
                data-testid="assets-upload-button"
                className="h-7 text-xs"
              >
                <Upload className="mr-1.5 h-3.5 w-3.5" /> <span className="hidden sm:inline">Upload Media</span><span className="sm:hidden">Upload</span>
              </Button>
              {(project.assets || []).length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadAllAssets}
                  data-testid="assets-download-all"
                  className="h-7 text-xs border-accent/40 bg-accent/10 text-accent hover:bg-accent hover:text-accent-foreground"
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" /> <span className="hidden sm:inline">Download All</span><span className="sm:hidden">All</span>
                </Button>
              )}
            </div>
          }
        >
          {(project.assets || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No saved assets yet. Record takes or snap photos above to save them here.</p>
          ) : (
            <ul className="divide-y divide-border" data-testid="asset-list">
              {(project.assets || []).map((a) => (
                <li
                  key={a.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 py-3"
                  data-testid={`asset-item-${a.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-foreground">{a.filename}</p>
                      {a.type === "video" && (
                        <span className="rounded bg-accent/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-accent shrink-0">
                          MP4
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {a.type}
                      {a.mime_type ? ` · ${a.mime_type}` : ""}
                      {a.size_bytes ? ` · ${(a.size_bytes / 1024 / 1024).toFixed(2)} MB` : ""}
                      {a.duration_ms ? ` · ${Math.round(a.duration_ms / 1000)}s` : ""}
                      {a.width && a.height ? ` · ${a.width}×${a.height}` : ""}
                      {" · "}
                      {a.source === "browser_recording" || a.source === "browser_capture"
                        ? "browser-local reference"
                        : a.source}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-auto">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadAsset(a)}
                      data-testid={`asset-download-${a.id}`}
                      className="h-8 text-xs hover:bg-accent hover:text-accent-foreground"
                      title="Download Asset"
                    >
                      <Download className="mr-1.5 h-3.5 w-3.5" /> Download
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteAsset(a.id)}
                      data-testid={`asset-delete-${a.id}`}
                      className="h-8 text-muted-foreground hover:text-destructive"
                      title="Delete Asset"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Saved assets can be downloaded directly in MP4 format to your device.
          </p>
        </SectionCard>

        {/* Notes */}
          <SectionCard
            title="Notes"
            icon={StickyNote}
            testid="section-notes"
            right={
              <Button
                size="sm"
                onClick={saveNotes}
                disabled={savingNotes || notes === (project.notes || "")}
                data-testid="notes-save"
                className="h-7 sm:h-8 text-xs"
              >
                <Save className="mr-1.5 h-3.5 w-3.5" />
                {savingNotes ? "Saving…" : "Save notes"}
              </Button>
            }
          >
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="B-roll ideas, timing notes, CTA reminders…"
              className="min-h-[140px] sm:min-h-[160px] w-full resize-y rounded-md border border-border bg-background p-3.5 sm:p-4 text-xs sm:text-sm text-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              data-testid="notes-input"
            />
          </SectionCard>

          {/* Download History */}
          <SectionCard title="Download History" icon={Download} testid="section-downloads">
            {downloads.length === 0 ? (
              <p className="text-sm text-muted-foreground">No downloads initiated yet.</p>
            ) : (
              <ul className="divide-y divide-border" data-testid="downloads-list">
                {downloads.map((d) => (
                  <li key={d.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 py-2 text-sm" data-testid={`download-item-${d.id}`}>
                    <span className="truncate font-medium text-xs sm:text-sm">{d.filename}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {formatDate(d.initiated_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              This log records only that a download was initiated in the browser. It cannot confirm the file was actually saved to disk.
            </p>
          </SectionCard>
        </div>

        {/* SIDEBAR column */}
        <div className="space-y-6 min-w-0">
          <SectionCard title="Progress" icon={Clapperboard} testid="section-progress">
            <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-elevated">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${STATUS_PROGRESS[project.status] || 0}%` }}
                data-testid="workspace-progress"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {STATUS_PROGRESS[project.status] || 0}% pipeline progress ({STATUS_LABEL[project.status]})
            </p>
            <p className="mt-3 text-xs text-muted-foreground">
              Status: <span className="text-foreground">{STATUS_LABEL[project.status]}</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Assets: <span className="text-foreground">{(project.assets || []).length}</span>
            </p>
            <p className="mt-3 text-xs text-muted-foreground">Updated {formatDate(project.updated_at)}</p>

            {project.status !== "ready" ? (
              <Button
                size="sm"
                className="mt-4 w-full bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => updateProject({ status: "ready" })}
                data-testid="workspace-mark-ready"
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Mark Ready for Publishing
              </Button>
            ) : (
              <div className="mt-4 rounded-md border border-accent/30 bg-accent/10 p-2.5 text-center text-xs font-medium text-accent">
                ✓ Ready for Publishing
              </div>
            )}
          </SectionCard>

          <SectionCard title="Timestamps" icon={CheckCircle2} testid="section-timestamps">
            <p className="text-xs text-muted-foreground">
              Created <span className="text-foreground">{formatDate(project.created_at)}</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Updated <span className="text-foreground">{formatDate(project.updated_at)}</span>
            </p>
          </SectionCard>
        </div>
      </div>
    </AppShell>
  );
}
